"use client"

// app/(public)/blogs/[slug]/page.tsx
// Individual blog post page - Spire Group

import { useState, useEffect } from "react"
import { useParams } from "next/navigation"
import Link from "next/link"
import Image from "next/image"
import Head from "next/head"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  Calendar,
  Clock,
  Eye,
  ArrowLeft,
  ArrowRight,
  Share2,
  BookOpen,
  Loader2
} from "lucide-react"

interface Blog {
  id: string
  slug: string
  title: string
  excerpt: string
  content: string
  featuredImage: string | null
  publishedAt: string
  metaTitle: string | null
  metaDescription: string | null
}

interface RelatedBlog {
  id: string
  slug: string
  title: string
  excerpt: string
  featuredImage: string | null
  publishedAt: string
}

export default function BlogPostPage() {
  const params = useParams()
  const slug = params.slug as string

  const [blog, setBlog] = useState<Blog | null>(null)
  const [relatedBlogs, setRelatedBlogs] = useState<RelatedBlog[]>([])
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    if (slug) {
      fetchBlog()
    }
  }, [slug])

  const fetchBlog = async () => {
    try {
      setLoading(true)
      const response = await fetch(`/api/blogs/${slug}`)
      const result = await response.json()

      if (result.success) {
        setBlog(result.data.blog)
        setRelatedBlogs(result.data.related || [])
      } else {
        setNotFound(true)
      }
    } catch (error) {
      console.error('Error fetching blog:', error)
      setNotFound(true)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const handleShare = () => {
    if (navigator.share && blog) {
      navigator.share({
        title: blog.title,
        text: blog.excerpt,
        url: window.location.href,
      })
    }
  }

  // Set document title and meta tags
  useEffect(() => {
    if (blog) {
      document.title = blog.metaTitle || `${blog.title} - Spire Group`

      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]')
      if (metaDescription) {
        metaDescription.setAttribute('content', blog.metaDescription || blog.excerpt || blog.title)
      } else {
        const newMeta = document.createElement('meta')
        newMeta.name = 'description'
        newMeta.content = blog.metaDescription || blog.excerpt || blog.title
        document.head.appendChild(newMeta)
      }
    }
  }, [blog])

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
        <span className="ml-3 text-lg text-gray-600">Loading article...</span>
      </div>
    )
  }

  if (notFound || !blog) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-8">
            The article you're looking for doesn't exist or has been removed.
          </p>
          <Link href="/blogs">
            <Button className="bg-accent hover:bg-accent/90 text-primary font-bold">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Mobile First with Better Contrast */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white overflow-hidden py-12 md:py-16 mt-16 md:mt-20">
        {/* Diagonal Lines Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, #d60500 35px, #d60500 36px)',
          }}></div>
        </div>

        {/* Red Accent Overlay */}
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-[#d60500]/20 to-transparent"></div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Back Button - Mobile Optimized */}
          <Link href="/blogs" className="inline-block mb-6 md:mb-8">
            <Button
              variant="outline"
              size="sm"
              className="border-[#d60500] bg-[#d60500] text-white hover:bg-white hover:text-[#d60500] transition-all"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              <span className="text-sm font-semibold">Back to Blog</span>
            </Button>
          </Link>

          {/* Title */}
          <h1 className="font-heading text-2xl md:text-3xl lg:text-5xl font-bold mb-4 md:mb-6 text-white leading-tight">
            {blog.title}
          </h1>

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-4 md:gap-6 text-white/90 mb-4 md:mb-6 text-sm md:text-base">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 md:w-5 md:h-5" />
              <span>{formatDate(blog.publishedAt)}</span>
            </div>
          </div>

          {/* Author & Share */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="text-[#d60500] font-semibold bg-white px-3 py-1 rounded-sm text-sm">By Spire Group</p>
            <Button
              onClick={handleShare}
              variant="outline"
              size="sm"
              className="border-[#d60500] bg-[#d60500] text-white hover:bg-white hover:text-[#d60500] transition-all"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      {blog.featuredImage && (
        <section className="relative h-96 md:h-[500px] bg-gray-100">
          <Image
            src={blog.featuredImage}
            alt={blog.metaTitle || blog.title}
            fill
            className="object-cover"
            priority
          />
        </section>
      )}

      {/* Article Content */}
      <article className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Excerpt */}
          {blog.excerpt && (
            <div className="text-xl text-gray-700 leading-relaxed mb-8 pb-8 border-b-2 border-accent/20">
              {blog.excerpt}
            </div>
          )}

          {/* Content */}
          <div
            className="prose prose-lg max-w-none
              prose-headings:font-heading prose-headings:text-primary prose-headings:font-bold
              prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6
              prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
              prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6
              prose-a:text-accent prose-a:no-underline hover:prose-a:underline
              prose-strong:text-primary prose-strong:font-bold
              prose-ul:my-6 prose-ol:my-6
              prose-li:text-gray-700 prose-li:mb-2
              prose-blockquote:border-l-4 prose-blockquote:border-accent prose-blockquote:pl-6 prose-blockquote:italic prose-blockquote:text-gray-700
              prose-img:rounded-sm prose-img:shadow-lg prose-img:my-8"
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />
        </div>
      </article>

      {/* Related Articles */}
      {relatedBlogs.length > 0 && (
        <section className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-heading font-bold text-primary mb-8">
              Related Articles
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedBlogs.map((relatedBlog) => (
                <Link
                  key={relatedBlog.id}
                  href={`/blogs/${relatedBlog.slug}`}
                  className="group"
                >
                  <article className="bg-white border-2 border-gray-200 rounded-sm overflow-hidden hover:border-accent hover:shadow-xl transition-all duration-300">
                    {/* Image */}
                    <div className="relative h-48 bg-gray-100 overflow-hidden">
                      {relatedBlog.featuredImage ? (
                        <Image
                          src={relatedBlog.featuredImage}
                          alt={relatedBlog.title}
                          fill
                          className="object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary to-primary/80">
                          <BookOpen className="w-12 h-12 text-accent" />
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <div className="flex items-center gap-3 text-sm text-gray-600 mb-3">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(relatedBlog.publishedAt)}</span>
                      </div>

                      <h3 className="text-lg font-bold text-primary mb-2 group-hover:text-accent transition-colors line-clamp-2">
                        {relatedBlog.title}
                      </h3>

                      {relatedBlog.excerpt && (
                        <p className="text-gray-600 text-sm line-clamp-2 mb-3">
                          {relatedBlog.excerpt}
                        </p>
                      )}

                      <div className="flex items-center gap-2 text-accent font-bold text-sm group-hover:gap-3 transition-all">
                        Read More
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  )
}
